import { MimeHeadersParser } from '../formats/mime/MimeHeadersParser';
import type { HeaderParseOptions } from '../mime-headers/parseHeaders.types';
import type { ParsedHeaderValue } from '../mime-headers/parseHeaderValueAndParams.types';
import type { StringDecoder } from '../types';
import { flatten, isStr } from '../utils/common';
import { ellipsize, escapeNonAscii } from '../utils/format';
import { type Warning, WarningCode } from '../warnings';
import { isKnownEpilogue, isKnownPreamble } from './isKnownPreamble';
import type { LeafMimeNode, MimeNode, MimeParsedMultipartBody, MultipartMimeNode } from './types';

const zendeskFailHeaders =
	'\r\nContent-Type: text/plain;\r\n charset=utf-8\r\nContent-Transfer-Encoding: quoted-printable\r\n\r\n';

export function parseMultipartMimeBody(
	body: string,
	boundary: string,
	decode: StringDecoder,
	treePos?: string
): MimeParsedMultipartBody {
	const selfWarnings: Warning[] = [];

	const parts = body.split('--' + boundary);

	// Re-join any false splits caused by an inner boundary that starts with the
	// outer boundary string (e.g. outer="bound", inner="bound_alt"). After a
	// false split the leftover piece starts with the suffix that distinguishes
	// the longer boundary (e.g. "_alt"), which is never a valid delimiter
	// terminator. Valid terminators: \r \n space tab (regular) or '-' (close).
	// Common case (no false splits): just increments i each iteration, no allocs.
	{
		let i = 1;
		while (i < parts.length) {
			const ch = parts[i].length > 0 ? parts[i][0] : undefined;
			if (
				ch !== '\r' &&
				ch !== '\n' &&
				ch !== ' ' &&
				ch !== '\t' &&
				ch !== '-' &&
				ch !== undefined
			) {
				parts[i - 1] += '--' + boundary + parts[i];
				parts.splice(i, 1);
			} else {
				i++;
			}
		}
	}

	// HACK / FIX for multipart bodies where the start boundary does not properly
	// begin with --
	if (parts.length > 0 && parts[0].startsWith(boundary)) {
		parts[0] = parts[0].slice(boundary.length);
		parts.unshift('');

		selfWarnings.push({
			code: WarningCode.MIME_MULTIPART_BAD_START_BOUNDARY,
			info: 'Multipart MIME node has bad start boundary (no start hyphens)',
		});
	}

	// HACK / FIX for multipart bodies where there is no end boundary
	/*
	if (parts.length === 2 && parts[0] === '' && !/^\s{0,10}--/.test(parts[1])) {
		// TODO: Test for this
		// Fake pushing a final end section
		parts.push('--');
		selfWarnings.push({
			code: WarningCode.MIME_MULTIPART_MISSING_END,
			info: 'Multipart MIME node has bad end (no boundary)',
		});
	}
	*/

	if (parts.length < 2) {
		throw new Error(
			`Found a multipart MIME body without close-delimiter (got ${parts.length} parts)`
		);
	}

	const first = parts.shift()!;
	let last = parts.pop()!;

	if (!last.startsWith('--')) {
		/*
		console.dir({
			first,
			last,
			parts,
		});
		*/
		throw new Error('Found a multipart MIME body without close-delimiter');
	}

	// A middle part starting with '--' is actually a close-delimiter that was
	// split in the middle because a second --boundary appears after it (e.g. a
	// ghost/empty attachment appended after the closing boundary by Exchange).
	// Treat the first such part as the real close, and fold everything from
	// that point onward (including the already-popped last) into the epilogue.
	{
		const earlyCloseIdx = parts.findIndex(p => p.startsWith('--'));
		if (earlyCloseIdx >= 0) {
			const tail = parts.splice(earlyCloseIdx);
			// Reconstruct the raw bytes that were split away: each element was
			// preceded by '--' + boundary in the original stream.
			const reconstructed =
				tail.map(p => '--' + boundary + p).join('') + '--' + boundary + last;
			last = reconstructed.startsWith('--' + boundary)
				? reconstructed.slice(('--' + boundary).length)
				: reconstructed;
		}
	}

	let epilogue: string;
	if (last.startsWith('--\r\n')) {
		epilogue = last.slice(4);
	} else if (last.startsWith('--\n')) {
		epilogue = last.slice(3);
	} else if (last.startsWith('--\r')) {
		epilogue = last.slice(3);
	} else if (last.startsWith('--')) {
		epilogue = last.slice(2);
		if (epilogue) {
			throw new Error('Close boundary does not end with newline?');
		}
	} else {
		throw new Error('Found a multipart MIME body with multiple close-delimiters?');
	}

	let preamble: string | null = null;
	if (first === '') {
		// Normal case, no preamble
	} else if (first.endsWith('\r\n')) {
		// Normal case with preamble
		preamble = first.slice(0, -2);
	} else if (first.endsWith('\n')) {
		// Normal case with preamble
		preamble = first.slice(0, -1);
	} else if (first.endsWith('\r')) {
		// Bare CR preamble
		preamble = first.slice(0, -1);
	} else {
		throw new Error('First boundary does not start with newline?');

		// needed?
		/*
		preamble = first;
		if (preamble) {
			throw new Error('First boundary does not start with newline?');
		}
		*/
	}

	const middleParts = parts.map(str => {
		if (str.startsWith('\r\n')) {
			str = str.slice(2);
		} else if (str.startsWith('\n')) {
			str = str.slice(1);
		} else if (str.startsWith('\r')) {
			str = str.slice(1);
		} else {
			throw new Error('Middle boundary does not end with newline');
		}

		if (str.endsWith('\r\n')) {
			str = str.slice(0, -2);
		} else if (str.endsWith('\n')) {
			str = str.slice(0, -1);
		} else if (str.endsWith('\r')) {
			str = str.slice(0, -1);
		} else {
			throw new Error('Middle boundary does not start with newline');
		}

		return str;
	});

	if (middleParts.length === 0) {
		selfWarnings.push({
			code: WarningCode.MIME_MULTIPART_0_BODIES,
			info: `Multipart MIME node has 0 bodies`,
		});
	}

	const childNodes = middleParts.map((c, idx) => {
		const childPos: string = treePos ? treePos + '.' + (idx + 1) : (idx + 1).toString();

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		return parseMimeTree(c, decode, undefined, childPos);
	});
	const descWarnings = flatten(childNodes.map(n => n.selfWarnings));

	if (preamble && !isKnownPreamble(preamble)) {
		selfWarnings.push({
			code: WarningCode.MIME_MULTIPART_PREAMBLE,
			info: `Multipart MIME node has non-empty preamble of ${
				preamble.length
			} bytes (${escapeNonAscii(ellipsize(preamble, 512))})`,
		});
	}

	if (epilogue && !isKnownEpilogue(epilogue)) {
		// Sometimes the epilogue is just all nulls
		selfWarnings.push({
			code: WarningCode.MIME_MULTIPART_EPILOGUE,
			info: `Multipart MIME node has non-empty epilogue of ${
				epilogue.length
			} bytes (${escapeNonAscii(ellipsize(epilogue, 512))})`,
		});
	}

	const node: MimeParsedMultipartBody = {
		selfWarnings: selfWarnings,
		allWarnings: [...selfWarnings, ...descWarnings],
		children: childNodes,
		preamble: preamble ? preamble : undefined,
		epilogue: epilogue ? epilogue : undefined,
	};

	return node;
}

/**
 * Parses a MIME tree struture
 * strictNameMode defaults true
 * @param mimePart
 * @param decode
 * @param rootHeaderParseOptions
 * @param treePos
 * @param expectHeaders
 * @returns
 */
export function parseMimeTree(
	mimePart: Buffer | string,
	decode: StringDecoder,
	rootHeaderParseOptions?: HeaderParseOptions & { strictNameMode?: boolean },
	treePos?: string
): MimeNode {
	mimePart = isStr(mimePart) ? mimePart : mimePart.toString('binary');

	let rawBody: string;

	// TODO: Remove? Not really used
	const selfHeaderWarnings: Warning[] = [];

	const headersParser = new MimeHeadersParser(
		decode,
		rootHeaderParseOptions?.strictNameMode ?? false,
		mimePart
	);

	if (headersParser.endStatus === 'double-nl') {
		// Normal situation with <headers><crlf><crlf><body>
		rawBody = mimePart.slice(headersParser.endPos);
	} else if (headersParser.endStatus === 'single-nl-eof') {
		// Empty body situation with <headers><crlf><eof>
		rawBody = mimePart.slice(headersParser.endPos);
	} else if (headersParser.endStatus === 'single-nl-start') {
		// Empty header situation with <crlf><body>
		rawBody = mimePart.slice(headersParser.endPos);
	} else if (headersParser.endStatus === 'empty') {
		// Empty node situation with
		rawBody = mimePart.slice(headersParser.endPos);
	} else {
		console.dir({
			end: headersParser.endPos,
			endDoubleNL: headersParser.endStatus,
			part: mimePart,
		});
		throw new Error('Headers did not end properly?');
	}

	let contentType: string | undefined;
	let contentTypeType: string | undefined;
	let contentTypeBoundary: string | undefined;
	let contentTypeName: string | undefined;
	let contentTypeCharset: string | undefined;

	let contentDisposition: string | undefined;
	let contentDispositionFilename: string | undefined;

	let contentLocation: string | undefined;

	// const rawContentTypeHeaders = rawHeaders['content-type'];
	// const rawContentTransEncHeaders = rawHeaders['content-transfer-encoding'];
	const contentTypeHeaders = headersParser.getHeadersDecodedWParams('content-type');
	const contentTransEncHeaders = headersParser.getHeadersDecoded('content-transfer-encoding');

	// Very hack for bad Zendesk email boundary
	if (
		contentTypeHeaders &&
		contentTransEncHeaders &&
		contentTypeHeaders.length === 2 &&
		contentTransEncHeaders.length === 2 &&
		contentTypeHeaders[0].value === 'multipart/alternative' &&
		contentTypeHeaders[0].params['boundary'] &&
		contentTypeHeaders[1].value === 'text/plain' &&
		headersParser.getHeadersRaw('x-mailer')?.at(-1) === 'Zendesk Mailer' &&
		mimePart.slice(0, headersParser.endPos).endsWith(zendeskFailHeaders)
	) {
		const boundary = contentTypeHeaders[0].params['boundary'];
		const repaired =
			mimePart.slice(0, headersParser.endPos - zendeskFailHeaders.length) +
			`\r\n\r\n--${boundary}` +
			zendeskFailHeaders +
			mimePart.slice(headersParser.endPos);
		const _result = parseMimeTree(repaired, decode, rootHeaderParseOptions, treePos);
		_result.allWarnings.push({
			code: WarningCode.MIME_MULTIPART_BAD_START_ZENDESK,
			info: 'Zendesk multipart/alternatie is missing entire start boundary',
		});
		return _result;
	}

	// Allow multiple content type headers if same...
	let _contentType: ParsedHeaderValue | undefined;
	if (!contentTypeHeaders || !contentTypeHeaders.length) {
		// noop
	} else if (contentTypeHeaders.length === 1) {
		_contentType = contentTypeHeaders[0];
	} else {
		// Multiple headers, see if all same
		const first = contentTypeHeaders[0];
		if (contentTypeHeaders.every(o => o.value === first.value)) {
			// All same (at least main value?)
			// Use last
			_contentType = contentTypeHeaders[contentTypeHeaders.length - 1];
		} else {
			throw new Error('Got multiple headers with name content-type');
		}
	}

	if (_contentType) {
		if (_contentType.value) {
			contentType = _contentType.value.toLowerCase().trim();
		}
		contentTypeType = _contentType.params['type'];
		contentTypeBoundary = _contentType.params['boundary'];
		contentTypeName = _contentType.params['name'];
		contentTypeCharset = _contentType.params['charset'];
	}

	const _contentDisposition = headersParser.getSingleAndDecodeWParams('content-disposition');

	if (_contentDisposition) {
		if (_contentDisposition.value) {
			contentDisposition = _contentDisposition.value.toLowerCase().trim();
		}
		contentDispositionFilename = _contentDisposition.params['filename'];
	}

	const contentId = headersParser.getSingleAndDecode('content-id');
	const transferEncoding = contentTransEncHeaders?.[0];

	const contentLocationHdr = headersParser.getSingleAndDecode('content-location');

	// Very long content locations may fold without a whitespace break, e.g. at 1000 chars,
	// and then the unfolding will leave extra whitespace.... remove it!
	const contentLocationStr = contentLocationHdr?.toLowerCase().replace(/\s+/g, '');

	if (contentLocationStr) {
		contentLocation = contentLocationStr;
	}

	const isMultipart = contentType?.startsWith('multipart/');

	if (
		isMultipart &&
		contentTypeBoundary &&
		contentDisposition &&
		contentDisposition !== 'inline'
	) {
		throw new Error(
			'Unhandled: got a "multipart/" node with boundary, but also got a content-disposition'
		);
	}

	if (isMultipart && !contentTypeBoundary && !contentDisposition) {
		throw new Error(
			'Got a "multipart/" node with no boundary, but also no content disposition?'
		);
	}

	if (contentType && isMultipart && contentTypeBoundary) {
		// First, split at close-delimiter
		const parsedBody = parseMultipartMimeBody(rawBody, contentTypeBoundary, decode, treePos);

		const selfWarnings2 = [...selfHeaderWarnings, ...parsedBody.selfWarnings];
		const allWarnings = [...selfHeaderWarnings, ...parsedBody.allWarnings];

		const node: MultipartMimeNode = {
			...parsedBody,
			mimeNodeType: 'multipart',
			treePos: treePos ?? 'root',
			selfWarnings: selfWarnings2,
			allWarnings: allWarnings,
			headers: headersParser,
			rawBodyLength: rawBody.length,

			contentType: contentType!,
			contentTypeType,
			contentTypeBoundary,
			contentTypeName,
			contentTypeCharset,

			contentDisposition,

			contentDispositionFilename,
			contentId,
			transferEncoding,
			contentLocation,
		};
		return node;
	} else {
		const node: LeafMimeNode = {
			mimeNodeType: 'leaf',
			treePos: treePos ?? 'root',
			selfWarnings: selfHeaderWarnings,
			allWarnings: selfHeaderWarnings,
			headers: headersParser,
			rawBodyLength: rawBody.length,
			rawBody: rawBody,

			contentType,
			contentTypeType,
			contentTypeBoundary,
			contentTypeName,
			contentTypeCharset,

			contentDisposition,
			contentDispositionFilename: contentDispositionFilename,
			contentId,
			transferEncoding,
			contentLocation,
		};

		return node;
	}
}
